import React from 'react'
import { Alert } from 'react-bootstrap'
import { ApplicationModel } from '../../../../models/ApplicationModel'
import { ApplicantModel } from '../../../../models/ApplicantModel'
import { Option } from '../../../../models/Option'
import {
  AddressField, EmailField, PhoneField, TextField, BooleanRadioButtonField, yesNoDataSource, yesNoDataSourceInverted, NumericSelectField, IField, validateFieldsEx, Label,
} from '../../../../shared/fields'
import { Salutation } from '../../../../shared/Utility'
import { ApplicationController } from '../../../../controllers/ApplicationController'
import { Backdrop } from '../../../../shared/Backdrop'

export interface ApplicantControlProps {
    model: ApplicationModel
    onChange: { (changes: Partial<ApplicationModel>, callback?: { (): any }): any }
    addressCountries: Option<string>[]
    preview? : boolean
    invalidEmailForAgent: boolean
}

export interface ApplicantControlState {
  loading: boolean
}

export class ApplicantControl extends React.Component<ApplicantControlProps, ApplicantControlState> implements IField {
  isApplicantPrimary: BooleanRadioButtonField
  salutation: NumericSelectField
  givenName: TextField
  familyName: TextField
  position: TextField
  phoneNumber: PhoneField
  mobileNumber: PhoneField
  email: EmailField
  webSite: TextField
  physicalAddress: AddressField
  postalAddress: AddressField
  isPostalDifferent: BooleanRadioButtonField
  isBusinessAddressDifferent : BooleanRadioButtonField
  businessAddress: AddressField
  otherSalutation: TextField
  container: HTMLDivElement
  ajax : ApplicationController

  constructor(props: ApplicantControlProps) {
    super(props)
    this.ajax = new ApplicationController()
    this.state = {
      loading: false,
    }
  }

  componentDidUpdate(prevProps: ApplicantControlProps) {
    const { model } = this.props
    if (!model.isConsultant) {
      if (model.applicantModel.isApplicantPrimary !== prevProps.model.applicantModel.isApplicantPrimary) {
        if (model.applicantModel.isApplicantPrimary) {
          this.ajax.getApplicantDetails(this, (d) => {
            this.onChange({
              givenName: d.givenName,
              familyName: d.familyName,
              position: null,
              email: null,
              phoneNumber: null,
            })
          })
        }
        if (model.applicantModel.isApplicantPrimary === false) {
          this.onChange({
            givenName: null,
            familyName: null,
            email: null,
            position: null,
            phoneNumber: null,
          })
        }
      }
    }
  }

  onChange = (change: Partial<ApplicantModel>, callback?: { (): any }) => {
    const { onChange, model } = this.props
    const applicantModel = { ...model.applicantModel, ...change }
    onChange({ applicantModel })
  }

  toggleSpinner(on: boolean, callback: { (): any }): void {
    this.setState({ loading: on }, callback)
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    validateFieldsEx(
      focus,
      callback,
      this.isApplicantPrimary,
      this.otherSalutation,
      this.givenName,
      this.familyName,
      this.position,
      this.email,
      this.phoneNumber,
      this.mobileNumber,
      this.physicalAddress,
      this.postalAddress,
      this.isPostalDifferent,
      this.isBusinessAddressDifferent,
      this.businessAddress,
      this.webSite,
    )
  }

  render() {
    const {
      model, addressCountries, preview, invalidEmailForAgent,
    } = this.props
    const { loading } = this.state

    const disablePrimaryApplicantFields = !model.isConsultant && model.applicantModel.isApplicantPrimary === null

    const salutationDataSource = [
      { value: Salutation.Mr, label: 'Mr' },
      { value: Salutation.Mrs, label: 'Mrs' },
      { value: Salutation.Ms, label: 'Ms' },
      { value: Salutation.Miss, label: 'Miss' },
      { value: Salutation.Other, label: 'Other' },
    ]

    return (
      <>
        <Label
          label="82. What is the name of the applicant? *"
          preview={preview}
        />
        <Alert className="emdg-page-alerts">
          The primary contact must be an authorised representative from the organisation applying for the grant, such as:
          <br />
          <br />
          - The Chief Executive Officer
          <br />
          - The Chief Financial Officer
          <br />
          - A Director
          <br />
          - The Chairman of the Board
          <br />
          - The Registered Company Secretary
          <br />
          - An Authorised Manager within a business, or
          <br />
          - The Applicant sole trader or partner.
          <br />
          <br />
          Because the grant agreement is between Austrade and the applicant, it must be entered into by the primary contact person in the applicant’s business and not by a third party or agent.
        </Alert>

        {!model.isConsultant && (
        <BooleanRadioButtonField
          ref={(r) => { this.isApplicantPrimary = r }}
          id="isApplicantPrimary"
          label="Are you the primary contact?"
          value={model.applicantModel.isApplicantPrimary}
          onChange={(isApplicantPrimary) => this.onChange({ isApplicantPrimary })}
          dataSource={yesNoDataSource}
          required={!model.isConsultant}
          preview={preview}
        />
        )}

        <NumericSelectField
          ref={(r) => { this.salutation = r }}
          id="applicantSalutation"
          label="Applicant Title"
          value={model.applicantModel.title}
          onChange={(title) => this.onChange({ title })}
          dataSource={salutationDataSource}
          block={false}
          required={false}
          preview={preview}
        />

        { model.applicantModel.title === Salutation.Other && (
        <TextField
          ref={(r) => { this.otherSalutation = r }}
          id="applicantOtherSalutation"
          label="Other Title"
          value={model.applicantModel.otherTitle}
          onChange={(otherTitle) => this.onChange({ otherTitle })}
          required={model.applicantModel.title === Salutation.Other}
          maxLength={50}
          preview={preview}
        />
        )}

        <TextField
          ref={(r) => { this.givenName = r }}
          id="applicantGiveName"
          label="Applicant First name"
          value={model.applicantModel.givenName}
          onChange={(givenName) => this.onChange({ givenName: givenName.trim() })}
          required
          maxLength={50}
          preview={preview}
          disabled={disablePrimaryApplicantFields}
        />

        <TextField
          ref={(r) => { this.familyName = r }}
          id="applicantFamilyName"
          label="Applicant Last name"
          value={model.applicantModel.familyName}
          onChange={(familyName) => this.onChange({ familyName: familyName.trim() })}
          maxLength={50}
          required
          preview={preview}
          disabled={disablePrimaryApplicantFields}
        />

        <TextField
          ref={(r) => { this.position = r }}
          id="applicantPosition"
          label="Applicant Position title"
          value={model.applicantModel.position}
          onChange={(position) => this.onChange({ position })}
          maxLength={50}
          required
          preview={preview}
          disabled={disablePrimaryApplicantFields}
        />

        <EmailField
          ref={(r) => { this.email = r }}
          id="applicantEmail"
          label="Applicant Email address"
          value={model.applicantModel.email}
          onChange={(email) => this.onChange({ email })}
          maxLength={80}
          required
          preview={preview}
          errorMessage={invalidEmailForAgent ? 'The primary contact details should be an authorised person from the company, and not the agent.' : null}
        />

        <PhoneField
          ref={(r) => { this.phoneNumber = r }}
          id="applicantPhone"
          label="Applicant Phone number (e.g. +61 2 9999 9999)"
          value={model.applicantModel.phoneNumber}
          onChange={(phoneNumber) => this.onChange({ phoneNumber })}
          required
          block={false}
          preview={preview}
          disabled={disablePrimaryApplicantFields}
        />

        <PhoneField
          ref={(r) => { this.mobileNumber = r }}
          id="applicantMobile"
          label="Applicant Mobile number (e.g. +61 499 999 999)"
          value={model.applicantModel.mobileNumber}
          onChange={(mobileNumber) => this.onChange({ mobileNumber })}
          block={false}
          preview={preview}
        />

        <AddressField
          ref={(r) => { this.physicalAddress = r }}
          id="applicantPhysicalAddress"
          label="Applicant Street address"
          value={model.applicantModel.physicalAddress}
          onChange={(physicalAddress) => this.onChange({ physicalAddress })}
          required
          australianAddressesOnly
          countries={addressCountries}
          preview={preview}
        />

        <BooleanRadioButtonField
          ref={(r) => { this.isPostalDifferent = r }}
          id="applicantPostalAddressDifferent"
          label="Is the postal address different to the one above?"
          value={model.applicantModel.isPostalDifferent}
          onChange={(isPostalDifferent) => this.onChange({ isPostalDifferent })}
          dataSource={yesNoDataSource}
          required
          preview={preview}
        />

        { model.applicantModel.isPostalDifferent && (
        <AddressField
          ref={(r) => { this.postalAddress = r }}
          id="applicantPostalAddress"
          label="Applicant Postal address"
          value={model.applicantModel.postalAddress}
          onChange={(postalAddress) => this.onChange({ postalAddress })}
          required={model.applicantModel.isPostalDifferent}
          australianAddressesOnly
          countries={addressCountries}
          preview={preview}
        />
        )}

        <BooleanRadioButtonField
          ref={(r) => { this.isBusinessAddressDifferent = r }}
          id="applicantBusinessAddressDifferent"
          label="83. Is the address stated in Question 82, your principal place of business in  Australia?"
          value={model.applicantModel.isBusinessAddressDifferent}
          onChange={(isBusinessAddressDifferent) => this.onChange({ isBusinessAddressDifferent })}
          dataSource={yesNoDataSourceInverted}
          required
          preview={preview}
          description={(
            <Alert className="emdg-page-alerts">
              For the purpose of this application, ‘principal place of business’ refers to where you currently employ the greatest number of people.
              {' '}
              For example, you may have a registered place of business in the Sydney CBD, but your manufacturing facility is in regional NSW where you employ 100 people.
              {' '}
              Therefore, your manufacturing facility is your principal place of business.
            </Alert>
          )}
        />

        { model.applicantModel.isBusinessAddressDifferent && (
        <AddressField
          ref={(r) => { this.postalAddress = r }}
          id="applicantBusinessAddress"
          label="Applicant Business address"
          value={model.applicantModel.businessAddress}
          onChange={(businessAddress) => this.onChange({ businessAddress })}
          required={model.applicantModel.isBusinessAddressDifferent}
          australianAddressesOnly
          countries={addressCountries}
          preview={preview}
        />
        )}
        <TextField
          ref={(r) => { this.webSite = r }}
          id="applicantWebSite"
          label="Applicant Website"
          value={model.applicantModel.webSite}
          onChange={(webSite) => this.onChange({ webSite })}
          required={model.applicantModel.isWebsitePresent}
          maxLength={80}
          preview={preview}
          description={(
            <Alert className="emdg-page-alerts">
              If your business has a website, you must provide the web address. If you do not have a website, please provide a URL link to a social media channel that you use for your business.
            </Alert>
          )}
        />

        <Backdrop on={loading} />
      </>
    )
  }
}
